import { useFormat } from '@shared/hooks/useFormat';
import { GlobalMessages } from '@shared/localization/messages.ts';
import axios from 'axios';
import dayjs from 'dayjs';
import type jsPDF from 'jspdf';
import type { CellDef, FontStyle } from 'jspdf-autotable';
import { useCallback } from 'react';

import { useMessages } from '@/hooks/messages';
import { sanitizeFilename } from '@/utils/fileSystem';
import { htmlToPlainText } from '@/utils/html';

import { hexToRgb } from './colorUtils';
import { exportMessageDefinitions } from './messages';
import type {
  ExportTable,
  HeaderFooterDataPromise,
  PdfExportProps,
  PdfFooter,
  PdfHeader,
} from './model';

interface PdfExportStyleConfig {
  tableHeaderFontColor?: string | null;
  tableHeaderBackgroundColor?: string | null;
  tableHeaderFontFamily?: string | null;
  tableHeaderBold?: boolean | null;
  tableHeaderItalic?: boolean | null;
  tableRowFontColor?: string | null;
  tableRowBackgroundColor?: string | null;
  tableRowFontFamily?: string | null;
  tableRowBold?: boolean | null;
  tableRowItalic?: boolean | null;
  tableSummaryFontColor?: string | null;
  tableSummaryBackgroundColor?: string | null;
  tableSummaryFontFamily?: string | null;
  tableSummaryBold?: boolean | null;
  tableSummaryItalic?: boolean | null;
}

export interface ImageSize {
  width: number;
  height: number;
}

async function fetchRobotoFontBase64() {
  const robotoFontBase64Response = await axios.get('/assets/fonts/roboto-regular-base64.txt', {
    responseType: 'text',
  });

  return robotoFontBase64Response.data;
}

export const getImageSize = async (imageUrl: string): Promise<ImageSize> => {
  const img = new Image();
  img.src = imageUrl;

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });
  return { width: img.width, height: img.height };
};

export const getScaledImageSize = (imageSize: ImageSize): ImageSize => {
  let scaledWidth = imageSize.width;
  let scaledHeight = imageSize.height;

  if (imageSize.width > HEADER_LOGO_MAX_WIDTH || imageSize.height > HEADER_LOGO_MAX_HEIGHT) {
    const widthRatio = HEADER_LOGO_MAX_WIDTH / imageSize.width;
    const heightRatio = HEADER_LOGO_MAX_HEIGHT / imageSize.height;
    const scale = Math.min(widthRatio, heightRatio);

    scaledWidth = imageSize.width * scale;
    scaledHeight = imageSize.height * scale;
  }
  return {
    width: scaledWidth,
    height: scaledHeight,
  };
};

export const MARGIN = 15;
export const HEADER_HEIGHT = 12;
export const FOOTER_HEIGHT = 12;
export const HEADER_LOGO_MAX_HEIGHT = HEADER_HEIGHT;
export const HEADER_LOGO_MAX_WIDTH = 30;
export const CONTENT_TOP_Y = HEADER_HEIGHT + MARGIN + 5;
export const CONTENT_BOTTOM_Y = FOOTER_HEIGHT + MARGIN;
export const CONTENT_VERTICAL_SPACING = 5;
export const HEADING_HEIGHT = 5;

export const usePdfExport = ({ orientation = 'portrait' }: PdfExportProps) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(exportMessageDefinitions);
  const { formatDateTime, formatNumber, formatBoolean } = useFormat(
    globalMessages.yes(),
    globalMessages.no(),
  );

  const getPageDimensions = useCallback((doc: jsPDF) => {
    return {
      pageWidth: doc.internal.pageSize.getWidth(),
      pageHeight: doc.internal.pageSize.getHeight(),
    };
  }, []);

  const addHeader = useCallback(
    async (doc: jsPDF, header: PdfHeader) => {
      const logoUrl = header?.lightModeLogoUrl;
      const logoSize = logoUrl ? await getImageSize(logoUrl) : undefined;

      doc.setFontSize(12);

      doc.text(header.tenantName, MARGIN, MARGIN + 5);

      const { pageWidth } = getPageDimensions(doc);

      if (header.projectName) {
        const projectNameWidth = doc.getTextWidth(header.projectName);
        doc.text(header.projectName, (pageWidth - projectNameWidth) / 2, MARGIN + 5);
      }

      const titleFontSize = 10;
      doc.setFontSize(titleFontSize);
      const titleWidth = doc.getTextWidth(header.title);
      doc.text(header.title, (pageWidth - titleWidth) / 2, MARGIN + 12);

      if (logoUrl && logoSize) {
        try {
          const scaledLogoSize = getScaledImageSize(logoSize);

          const logoX = pageWidth - MARGIN - scaledLogoSize.width;
          const logoY = MARGIN;

          doc.addImage(logoUrl, logoX, logoY, scaledLogoSize.width, scaledLogoSize.height);
        } catch (error) {
          console.error('Failed to load logo:', error);
        }
      }
    },
    [getPageDimensions],
  );

  const addFooter = useCallback(
    (doc: jsPDF, footer: PdfFooter | undefined, pageNumber: number, totalPages: number) => {
      doc.setFontSize(8);

      const footerLine1Left = footer ? footer.estimationOwnerName : '';
      const footerLine1Center = footer
        ? `v${footer.estimationVersionNumber} - ${footer.estimationVersionName}`
        : '';
      const footerLine1Right = formatDateTime(new Date());

      const { pageWidth, pageHeight } = getPageDimensions(doc);
      doc.text(footerLine1Left, MARGIN, pageHeight - 20);
      const footerLine1CenterWidth = doc.getTextWidth(footerLine1Center);
      doc.text(footerLine1Center, (pageWidth - footerLine1CenterWidth) / 2, pageHeight - 20);
      const footerLine1RightWidth = doc.getTextWidth(footerLine1Right);
      doc.text(
        footerLine1Right,
        pageWidth - MARGIN - footerLine1RightWidth,
        pageHeight - (footer?.estimationOwnerName || footer?.estimationVersionNumber ? 20 : 15),
      );

      const footerLine2Center = messages.pdfPageNOfM({
        currentPageNumber: formatNumber(pageNumber, true),
        totalPagesCount: formatNumber(totalPages, true),
      });
      const footerLine2CenterWidth = doc.getTextWidth(footerLine2Center);
      doc.text(footerLine2Center, (pageWidth - footerLine2CenterWidth) / 2, pageHeight - 15);

      doc.setFontSize(6);
      const footerLine3Right = messages.pdfPoweredBy();
      const footerLine3RightWidth = doc.getTextWidth(footerLine3Right);
      doc.text(footerLine3Right, pageWidth - MARGIN - footerLine3RightWidth, pageHeight - 10);
    },
    [formatDateTime, formatNumber, getPageDimensions, messages],
  );

  const exportPdf = useCallback(
    async (
      filename: string,
      tables: ExportTable[],
      headerFooterDataPromise: HeaderFooterDataPromise,
      styleConfig?: PdfExportStyleConfig,
    ) => {
      // EWM-1048: jspdf + jspdf-autotable are ~347 kB minified. Fetched on demand so they are
      // not paid for by everyone who merely renders a table.
      const [{ default: JsPDF }, { default: autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
      ]);
      const doc = new JsPDF({ orientation, format: 'a4' });

      const robotoFontBase64: string = await fetchRobotoFontBase64();
      doc.addFileToVFS('Roboto-Regular.ttf', robotoFontBase64);
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.setDisplayMode('fullwidth');
      doc.setFont('Roboto', 'normal');

      const { pageHeight } = getPageDimensions(doc);

      const { header, footer } = await headerFooterDataPromise;

      let currentY = CONTENT_TOP_Y;

      tables.forEach((table, tablesIndex) => {
        const { rows, columns, title } = table;
        const tableRows = rows.map((row) =>
          columns.map((col) => {
            const cellValue = row[col.accessor] ?? '';
            if (col.isHtml) {
              if (typeof cellValue !== 'string') {
                throw new Error('Html cell must be of type string');
              }
              return htmlToPlainText(cellValue);
            }
            if (dayjs.isDayjs(cellValue)) {
              return formatDateTime(cellValue);
            }

            return cellValue;
          }),
        );
        const tableHeaders = table.columns.map<CellDef>((column) => ({
          title: column.headerLabel,
          styles: {
            halign: table.rows.every(
              (row) =>
                typeof row[column.accessor] === 'number' ||
                row[column.accessor] === undefined ||
                row[column.accessor] === null,
            )
              ? 'right'
              : 'left',
          },
        }));

        if (title) {
          // Blank line between page-header subtitle and the section heading (EWM-628 1c)
          currentY += CONTENT_VERTICAL_SPACING;
          doc.text(title, MARGIN, currentY);
          currentY += HEADING_HEIGHT + CONTENT_VERTICAL_SPACING;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const headStyles: any = {};
        if (styleConfig?.tableHeaderBackgroundColor) {
          headStyles.fillColor = hexToRgb(styleConfig.tableHeaderBackgroundColor);
        } else {
          headStyles.fillColor = [40, 127, 186];
        }
        if (styleConfig?.tableHeaderFontColor) {
          headStyles.textColor = hexToRgb(styleConfig.tableHeaderFontColor);
        }
        if (styleConfig?.tableHeaderFontFamily) {
          headStyles.font = styleConfig.tableHeaderFontFamily;
        }
        if (styleConfig?.tableHeaderBold || styleConfig?.tableHeaderItalic) {
          const fontName = styleConfig.tableHeaderFontFamily || 'Roboto';
          const standardFont = fontName.toLowerCase().includes('arial')
            ? 'helvetica'
            : fontName.toLowerCase().includes('times')
              ? 'times'
              : fontName.toLowerCase().includes('courier')
                ? 'courier'
                : 'helvetica';

          if (standardFont !== fontName) {
            headStyles.font = standardFont;
          }

          if (styleConfig.tableHeaderBold && styleConfig.tableHeaderItalic) {
            headStyles.fontStyle = 'bolditalic';
          } else if (styleConfig.tableHeaderBold) {
            headStyles.fontStyle = 'bold';
          } else if (styleConfig.tableHeaderItalic) {
            headStyles.fontStyle = 'italic';
          }
        }

        const bodyStyles: Record<string, unknown> = {};
        if (styleConfig?.tableRowBackgroundColor) {
          bodyStyles.fillColor = hexToRgb(styleConfig.tableRowBackgroundColor);
        }
        if (styleConfig?.tableRowFontColor) {
          bodyStyles.textColor = hexToRgb(styleConfig.tableRowFontColor);
        }
        if (styleConfig?.tableRowFontFamily) {
          bodyStyles.font = styleConfig.tableRowFontFamily;
        }
        if (styleConfig?.tableRowBold || styleConfig?.tableRowItalic) {
          if (styleConfig.tableRowBold && styleConfig.tableRowItalic) {
            bodyStyles.fontStyle = 'bolditalic';
          } else if (styleConfig.tableRowBold) {
            bodyStyles.fontStyle = 'bold';
          } else if (styleConfig.tableRowItalic) {
            bodyStyles.fontStyle = 'italic';
          }
        }

        // A row is a summary/totals row only when its first column literally reads "Total"
        // or "Summe"/Sum-equivalent — never just because it is the last row of an arbitrary
        // table (key/value tables like Masterdata would otherwise gain spurious bold on the
        // final field, see EWM-628 1a).
        const isSummaryRow = (
          _rowIndex: number,
          _totalRows: number,
          firstColumnValue: unknown,
        ): boolean => {
          if (firstColumnValue && typeof firstColumnValue === 'string') {
            const firstColLower = firstColumnValue.toLowerCase();
            return firstColLower.includes('total') || firstColLower.includes('sum');
          }
          return false;
        };

        autoTable(doc, {
          startY: currentY,
          margin: {
            top: CONTENT_TOP_Y,
            bottom: CONTENT_BOTTOM_Y,
          },
          head: [tableHeaders],
          body: tableRows,
          styles: {
            font: styleConfig?.tableRowFontFamily || 'Roboto',
            fontSize: 5,
            cellWidth: 'auto',
          },
          headStyles,
          bodyStyles: Object.keys(bodyStyles).length > 0 ? bodyStyles : undefined,
          theme: 'grid',
          didParseCell: function (data) {
            const { cell, row } = data;
            const content = cell.raw;

            const rowIndex = row.index;
            const totalRows = data.table.body.length;
            const firstColumnValue =
              Array.isArray(row.raw) && row.raw.length > 0 ? row.raw[0] : null;
            const isSummary = isSummaryRow(rowIndex, totalRows, firstColumnValue);

            if (styleConfig) {
              if (isSummary) {
                if (styleConfig.tableSummaryBackgroundColor) {
                  cell.styles.fillColor = hexToRgb(styleConfig.tableSummaryBackgroundColor);
                }
                if (styleConfig.tableSummaryFontColor) {
                  cell.styles.textColor = hexToRgb(styleConfig.tableSummaryFontColor);
                }
                if (styleConfig.tableSummaryFontFamily) {
                  cell.styles.font = styleConfig.tableSummaryFontFamily;
                }
                if (styleConfig.tableSummaryBold || styleConfig.tableSummaryItalic) {
                  const fontName = styleConfig.tableSummaryFontFamily || 'Roboto';
                  const standardFont = fontName.toLowerCase().includes('arial')
                    ? 'helvetica'
                    : fontName.toLowerCase().includes('times')
                      ? 'times'
                      : fontName.toLowerCase().includes('courier')
                        ? 'courier'
                        : 'helvetica';

                  let fontStyle: FontStyle = 'normal';
                  if (styleConfig.tableSummaryBold && styleConfig.tableSummaryItalic) {
                    fontStyle = 'bolditalic';
                  } else if (styleConfig.tableSummaryBold) {
                    fontStyle = 'bold';
                  } else if (styleConfig.tableSummaryItalic) {
                    fontStyle = 'italic';
                  }

                  cell.styles.font = standardFont;
                  cell.styles.fontStyle = fontStyle;
                }
              } else {
                if (styleConfig.tableRowBold || styleConfig.tableRowItalic) {
                  const fontName = styleConfig.tableRowFontFamily || 'Roboto';
                  const standardFont = fontName.toLowerCase().includes('arial')
                    ? 'helvetica'
                    : fontName.toLowerCase().includes('times')
                      ? 'times'
                      : fontName.toLowerCase().includes('courier')
                        ? 'courier'
                        : 'helvetica';

                  let fontStyle: FontStyle = 'normal';
                  if (styleConfig.tableRowBold && styleConfig.tableRowItalic) {
                    fontStyle = 'bolditalic';
                  } else if (styleConfig.tableRowBold) {
                    fontStyle = 'bold';
                  } else if (styleConfig.tableRowItalic) {
                    fontStyle = 'italic';
                  }

                  cell.styles.font = standardFont;
                  cell.styles.fontStyle = fontStyle;
                }
              }
            }

            if (typeof content === 'number') {
              cell.styles.halign = 'right';
              cell.text = [formatNumber(content)];
            } else if (typeof content === 'boolean') {
              cell.styles.halign = 'left';
              cell.text = [formatBoolean(content)];
            }
          },
          didDrawPage: function (data) {
            currentY = data.cursor?.y ?? CONTENT_TOP_Y;

            if (currentY + CONTENT_BOTTOM_Y > pageHeight) {
              doc.addPage();
              currentY = CONTENT_TOP_Y;
            }
          },
        });

        const isLastPage = tablesIndex === tables.length - 1;
        if (!isLastPage) {
          doc.addPage();
          currentY = CONTENT_TOP_Y;
        }
      });

      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, header);
        addFooter?.(doc, footer, i, totalPages);
      }

      doc.save(sanitizeFilename(`${filename}.pdf`));
    },
    [
      orientation,
      getPageDimensions,
      formatDateTime,
      formatNumber,
      formatBoolean,
      addHeader,
      addFooter,
    ],
  );

  return { exportPdf, addHeader, addFooter };
};
